/**
 * Host Global - Property Rental Management System
 * Admin Dashboard Application
 */

import { useState, useEffect, useMemo, useRef } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";

// Components
import MDBox from "components/MDBox";

// Layout components
import Sidenav from "examples/Sidenav";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes
import { getRoutes } from "routes";
import PropertyDetails from "layouts/admin/properties/details";
import AssignInspector from "layouts/admin/properties/assign-inspector";
import PromoCodesManagement from "layouts/admin/promo-codes";
import InspectorAnalytics from "layouts/admin/operations-team/inspector-analytics";
import AgentAnalytics from "layouts/admin/operations-team/agent-analytics";
import InspectionDetail from "layouts/operations/inspections/detail";
import InspectionReview from "layouts/super-admin/inspections/review";
import CreateUser from "layouts/super-admin/users/create";
import UserProfile from "layouts/super-admin/users/profile";
import PropertyOwnerProfile from "layouts/super-admin/property-owners/profile";
import SuperAdminReservationDetails from "layouts/super-admin/reservations/details";
import SignIn from "layouts/authentication/sign-in";
import VerifyEmail from "layouts/authentication/verify-email";
import ForgotPassword from "layouts/authentication/forgot-password";
import ResetPassword from "layouts/authentication/reset-password";

// Contexts
import { useMaterialUIController, setMiniSidenav } from "context";
import { useAuth } from "context/authContext";

// Global floating buttons (WhatsApp + Go to Top) – rendered at root so they stay above all content
import FloatingButtons from "components/FloatingButtons";

// Root redirect: "/" -> login or role-based dashboard
function RootRedirect() {
  const { isAuthenticated, loading, userRole } = useAuth();
  if (loading) {
    return (
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ bgcolor: "background.default" }}
      >
        <CircularProgress />
      </MDBox>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/authentication/sign-in" replace />;
  }
  if (userRole === "Super_Admin") {
    return <Navigate to="/super-admin/dashboard" replace />;
  }
  if (userRole === "Call_Center_Agent") {
    return <Navigate to="/call-center/dashboard" replace />;
  }
  if (userRole === "Property_Owner" || userRole === "Property Owner") {
    return <Navigate to="/property-owner/properties" replace />;
  }
  return <Navigate to="/admin/dashboard" replace />;
}

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { userRole, isAuthenticated, loading, user, logout } = useAuth();
  const navigate = useNavigate();
  const lastRedirectRef = useRef(null);

  // Get routes based on user role
  const routes = getRoutes(userRole);

  // Protected routes that require authentication
  const protectedRoutes = [
    "/dashboard",
    "/super-admin/dashboard",
    "/super-admin/property-owners",
    "/super-admin/properties",
    "/super-admin/packages",
    "/super-admin/users",
    "/super-admin/users/create",
    "/super-admin/users/profile/:id",
    "/super-admin/property-owners/profile/:id",
    "/super-admin/inspections",
    "/super-admin/inspections/review/:id",
    "/super-admin/amenities-criteria",
    "/admin",
    "/admin/dashboard",
    "/admin/properties",
    "/admin/property-owners",
    "/admin/operations-team",
    "/admin/inspections",
    "/admin/promo-codes",
    "/admin/analytics",
    "/admin/housekeeping",
    "/housekeeping/tasks",
    "/admin/closure-requests",
    "/admin/call-center",
    "/admin/items-to-process",
    "/admin/reservations",
    "/super-admin/closure-requests",
    "/super-admin/early-checkout",
    "/super-admin/items-to-process",
    "/super-admin/call-center",
    "/operations",
    "/operations/inspections",
    "/call-center",
    "/call-center/dashboard",
    "/financial",
    "/bookings",
    "/checkin-out",
    "/operations/checkin-out",
    "/housekeeping",
    "/marketing",
    "/call-center",
    "/property-owner",
    "/property-owner/properties",
    "/property-owner/properties/add",
    "/property-owner/reservations",
    "/property-owner/reservations/:id",
    "/property-owner/wallet",
    "/property-owner/closure-requests",
    "/admin/properties",
    "/admin/properties/:id",
    "/admin/properties/:id/assign-inspector",
    "/profile",
  ];

  // Handle authentication and redirects
  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) {
      lastRedirectRef.current = null;
      return;
    }

    // Check if user is a client and reject access
    if (user && (user.type === "client" || (!user.role && !user.fullName))) {
      // Client detected - log them out and redirect to sign-in
      logout();
      if (pathname !== "/authentication/sign-in") {
        navigate("/authentication/sign-in", { replace: true });
      }
      return;
    }

    // Create a unique key for this redirect check
    const redirectKey = `${pathname}-${isAuthenticated}-${userRole}`;

    // Prevent duplicate redirects for the same state
    if (lastRedirectRef.current === redirectKey) {
      return;
    }

    const isProtectedRoute = protectedRoutes.some((route) => pathname.startsWith(route));
    const isAuthRoute = pathname.startsWith("/authentication");

    // If user is authenticated and on login page, redirect to appropriate dashboard
    if (isAuthenticated && isAuthRoute) {
      let targetRoute = "/dashboard";
      if (userRole === "Super_Admin") {
        targetRoute = "/super-admin/dashboard";
      } else if (userRole === "Call_Center_Agent") {
        targetRoute = "/call-center/dashboard";
      } else if (userRole === "Property_Owner" || userRole === "Property Owner") {
        targetRoute = "/dashboard";
      }
      // Only redirect if not already on target route
      if (pathname !== targetRoute) {
        lastRedirectRef.current = redirectKey;
        navigate(targetRoute, { replace: true });
      }
      return;
    }

    // If user is not authenticated and trying to access protected route, redirect to login
    // But allow access to authentication routes (sign-in, sign-up) and verify-email
    const isVerifyEmailRoute = pathname.startsWith("/verify-email");
    if (!isAuthenticated && isProtectedRoute && !isAuthRoute && !isVerifyEmailRoute) {
      // Only redirect if not already on login page
      if (pathname !== "/authentication/sign-in" && pathname !== "/authentication/sign-up") {
        lastRedirectRef.current = redirectKey;
        navigate("/authentication/sign-in", { replace: true });
      }
      return;
    }

    // Allow unauthenticated users to access authentication routes and verify-email
    if (!isAuthenticated && (isAuthRoute || isVerifyEmailRoute)) {
      // User is on auth route and not authenticated - allow it
      lastRedirectRef.current = null;
      return;
    }

    // Redirect Super Admin from /dashboard to /super-admin/dashboard
    if (isAuthenticated && userRole === "Super_Admin" && pathname === "/dashboard") {
      lastRedirectRef.current = redirectKey;
      navigate("/super-admin/dashboard", { replace: true });
      return;
    }

    // Reset redirect tracking if no redirect was needed
    lastRedirectRef.current = null;
  }, [pathname, isAuthenticated, loading, userRole, navigate, user, logout]); // Dependencies for redirect logic

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const renderRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return (
          <>
            {renderRoutes(route.collapse)}
            {route.route && (
              <Route exact path={route.route} element={route.component} key={route.key} />
            )}
          </>
        );
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName=""
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          </>
        )}
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          {renderRoutes(routes)}
          <Route path="/authentication/sign-in" element={<SignIn />} />
          <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
          <Route path="/authentication/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/admin/properties/:id" element={<PropertyDetails />} />
          <Route path="/admin/properties/:id/assign-inspector" element={<AssignInspector />} />
          <Route path="/admin/promo-codes" element={<PromoCodesManagement />} />
          <Route
            path="/admin/operations-team/inspector/:inspectorId"
            element={<InspectorAnalytics />}
          />
          <Route path="/admin/operations-team/agent/:agentId" element={<AgentAnalytics />} />
          <Route path="/operations/inspections/:id" element={<InspectionDetail />} />
          <Route path="/super-admin/inspections/review/:id" element={<InspectionReview />} />
          <Route path="/super-admin/users/create" element={<CreateUser />} />
          <Route path="/super-admin/reservations/:id" element={<SuperAdminReservationDetails />} />
          <Route path="/admin/reservations/:id" element={<SuperAdminReservationDetails />} />
        </Routes>
        <FloatingButtons />
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName=""
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        {renderRoutes(routes)}
        <Route path="/authentication/sign-in" element={<SignIn />} />
        <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
        <Route path="/authentication/reset-password/:token" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/admin/properties/:id" element={<PropertyDetails />} />
        <Route path="/admin/properties/:id/assign-inspector" element={<AssignInspector />} />
        <Route path="/admin/promo-codes" element={<PromoCodesManagement />} />
        <Route
          path="/admin/operations-team/inspector/:inspectorId"
          element={<InspectorAnalytics />}
        />
        <Route path="/admin/operations-team/agent/:agentId" element={<AgentAnalytics />} />
        <Route path="/operations/inspections/:id" element={<InspectionDetail />} />
        <Route path="/super-admin/inspections/review/:id" element={<InspectionReview />} />
        <Route path="/super-admin/users/create" element={<CreateUser />} />
        <Route path="/super-admin/users/profile/:id" element={<UserProfile />} />
        <Route path="/super-admin/property-owners/profile/:id" element={<PropertyOwnerProfile />} />
        <Route path="/super-admin/reservations/:id" element={<SuperAdminReservationDetails />} />
        <Route path="/admin/reservations/:id" element={<SuperAdminReservationDetails />} />
      </Routes>
      <FloatingButtons />
    </ThemeProvider>
  );
}
